import { useState, useEffect } from 'react';
import { X, Plus, ArrowUpRight, RotateCcw, History, Shirt, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GarmentItemDetailModal({ item, onClose, onRefresh }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('types');
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeQty, setNewTypeQty] = useState(0);
  
  // Add stock state
  const [selectedTypeForStock, setSelectedTypeForStock] = useState(null);
  const [addStockQty, setAddStockQty] = useState(1);
  const [addStockRemarks, setAddStockRemarks] = useState('');

  // Dispatch state
  const [selectedTypeForDispatch, setSelectedTypeForDispatch] = useState(null);
  const [dispatchQty, setDispatchQty] = useState(1);
  const [dispatchParty, setDispatchParty] = useState('');
  const [dispatchRemarks, setDispatchRemarks] = useState('');

  // Return state
  const [selectedTypeForReturn, setSelectedTypeForReturn] = useState(null);
  const [returnQty, setReturnQty] = useState(1);
  const [returnParty, setReturnParty] = useState('');
  const [returnRemarks, setReturnRemarks] = useState('');

  useEffect(() => {
    fetchGarmentTypes();
    fetchMovements();
  }, [item.id]);

  const fetchGarmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/garment/inventory/${item.id}/types`);
      setGarmentTypes(response.data.garment_types || []);
    } catch (error) {
      console.error('Error fetching garment types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API}/garment/movement/${item.id}?limit=50`);
      setMovements(response.data.movements || []);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Please enter a garment type name');
      return;
    }

    try {
      await axios.post(`${API}/garment/inventory/${item.id}/type`, {
        garment_inventory_id: item.id,
        garment_type: newTypeName.trim(),
        quantity: newTypeQty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Added garment type: ${newTypeName}`);
      setNewTypeName('');
      setNewTypeQty(0);
      setShowAddType(false);
      fetchGarmentTypes();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add garment type');
    }
  };

  const handleAddStock = async (garmentType) => {
    if (addStockQty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await axios.patch(
        `${API}/garment/inventory/${item.id}/type/${encodeURIComponent(garmentType)}/add_stock`,
        { quantity: addStockQty, remarks: addStockRemarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added ${addStockQty} pieces to ${garmentType}`);
      setSelectedTypeForStock(null);
      setAddStockQty(1);
      setAddStockRemarks('');
      fetchGarmentTypes();
      fetchMovements();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stock');
    }
  };

  const handleDispatch = async (garmentType) => {
    if (dispatchQty <= 0 || !dispatchParty.trim()) {
      toast.error('Please enter quantity and party name');
      return;
    }

    try {
      await axios.post(
        `${API}/garment/dispatch?item_id=${item.id}`,
        {
          garment_type: garmentType,
          quantity: dispatchQty,
          party_name: dispatchParty.trim(),
          remarks: dispatchRemarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Dispatched ${dispatchQty} ${garmentType} to ${dispatchParty}`);
      setSelectedTypeForDispatch(null);
      setDispatchQty(1);
      setDispatchParty('');
      setDispatchRemarks('');
      fetchGarmentTypes();
      fetchMovements();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch');
    }
  };

  const handleReturn = async (garmentType) => {
    if (returnQty <= 0 || !returnParty.trim()) {
      toast.error('Please enter quantity and party name');
      return;
    }

    try {
      await axios.post(
        `${API}/garment/return?item_id=${item.id}`,
        {
          garment_type: garmentType,
          quantity: returnQty,
          party_name: returnParty.trim(),
          remarks: returnRemarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Returned ${returnQty} ${garmentType} from ${returnParty}`);
      setSelectedTypeForReturn(null);
      setReturnQty(1);
      setReturnParty('');
      setReturnRemarks('');
      fetchGarmentTypes();
      fetchMovements();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process return');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-4">
            {item.photo_url ? (
              <img
                src={`${BACKEND_URL}${item.photo_url}`}
                alt={item.fabric_name}
                className="w-16 h-16 object-cover rounded-xl border-2 border-white shadow"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                <Shirt className="w-8 h-8 text-slate-400" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-800">{item.fabric_name}</h2>
              <p className="text-sm text-slate-500 font-mono">{item.qr_code || item.fabric_code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-slate-100"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50">
          <div className="bg-white rounded-xl p-4 text-center border">
            <p className="text-2xl font-bold text-emerald-600">{item.total_available}</p>
            <p className="text-xs text-slate-500 uppercase">Available</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center border">
            <p className="text-2xl font-bold text-amber-600">{item.total_outside}</p>
            <p className="text-xs text-slate-500 uppercase">Outside</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center border">
            <p className="text-2xl font-bold text-slate-600">{item.total_pieces}</p>
            <p className="text-xs text-slate-500 uppercase">Total</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-2 bg-slate-100">
          <button
            onClick={() => setActiveTab('types')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              activeTab === 'types' ? 'bg-white text-amber-700 shadow' : 'text-slate-600'
            }`}
          >
            <Package className="w-4 h-4" />
            Garment Types
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              activeTab === 'history' ? 'bg-white text-amber-700 shadow' : 'text-slate-600'
            }`}
          >
            <History className="w-4 h-4" />
            Movement History
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
          {activeTab === 'types' && (
            <div className="space-y-3">
              {/* Add Type Button */}
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowAddType(!showAddType)}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Garment Type
                </Button>
              </div>

              {/* Add Type Form */}
              {showAddType && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 space-y-3">
                  <h4 className="font-medium text-amber-800">New Garment Type</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Type name (e.g., Trouser, Shirt)"
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Initial quantity"
                      value={newTypeQty}
                      onChange={(e) => setNewTypeQty(parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAddType(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleAddType} className="bg-amber-600 hover:bg-amber-700">
                      Add Type
                    </Button>
                  </div>
                </div>
              )}

              {/* Garment Types List */}
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : garmentTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Shirt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No garment types added yet</p>
                  <p className="text-sm">Click &quot;Add Garment Type&quot; to get started</p>
                </div>
              ) : (
                garmentTypes.map((gt) => (
                  <div key={gt.id} className="bg-white rounded-xl border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">{gt.garment_type}</h4>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => setSelectedTypeForStock(gt.garment_type)}
                        >
                          <Plus className="w-3 h-3" />
                          Stock
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                          onClick={() => setSelectedTypeForDispatch(gt.garment_type)}
                          disabled={gt.quantity_available <= 0}
                        >
                          <ArrowUpRight className="w-3 h-3" />
                          Dispatch
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => setSelectedTypeForReturn(gt.garment_type)}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Return
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-emerald-50 rounded-lg p-2">
                        <p className="text-xl font-bold text-emerald-600">{gt.quantity_available}</p>
                        <p className="text-xs text-emerald-700">Available</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-2">
                        <p className="text-xl font-bold text-amber-600">{gt.quantity_outside}</p>
                        <p className="text-xs text-amber-700">Outside</p>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-2">
                        <p className="text-xl font-bold text-slate-600">{gt.total_pieces}</p>
                        <p className="text-xs text-slate-700">Total</p>
                      </div>
                    </div>

                    {/* Add Stock Form */}
                    {selectedTypeForStock === gt.garment_type && (
                      <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <h5 className="font-medium text-emerald-800 mb-2">Add Stock to {gt.garment_type}</h5>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            placeholder="Quantity"
                            value={addStockQty}
                            onChange={(e) => setAddStockQty(parseInt(e.target.value) || 0)}
                            min="1"
                          />
                          <Input
                            placeholder="Remarks (optional)"
                            value={addStockRemarks}
                            onChange={(e) => setAddStockRemarks(e.target.value)}
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedTypeForStock(null)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleAddStock(gt.garment_type)} className="bg-emerald-600 hover:bg-emerald-700">
                            Add Stock
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Dispatch Form */}
                    {selectedTypeForDispatch === gt.garment_type && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <h5 className="font-medium text-amber-800 mb-2">Dispatch {gt.garment_type}</h5>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={dispatchQty}
                            onChange={(e) => setDispatchQty(parseInt(e.target.value) || 0)}
                            min="1"
                            max={gt.quantity_available}
                          />
                          <Input
                            placeholder="Party Name *"
                            value={dispatchParty}
                            onChange={(e) => setDispatchParty(e.target.value)}
                          />
                          <Input
                            placeholder="Remarks"
                            value={dispatchRemarks}
                            onChange={(e) => setDispatchRemarks(e.target.value)}
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedTypeForDispatch(null)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleDispatch(gt.garment_type)} className="bg-amber-600 hover:bg-amber-700">
                            Dispatch
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Return Form */}
                    {selectedTypeForReturn === gt.garment_type && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-medium text-blue-800 mb-2">Return {gt.garment_type}</h5>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={returnQty}
                            onChange={(e) => setReturnQty(parseInt(e.target.value) || 0)}
                            min="1"
                          />
                          <Input
                            placeholder="Party Name *"
                            value={returnParty}
                            onChange={(e) => setReturnParty(e.target.value)}
                          />
                          <Input
                            placeholder="Remarks"
                            value={returnRemarks}
                            onChange={(e) => setReturnRemarks(e.target.value)}
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedTypeForReturn(null)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleReturn(gt.garment_type)} className="bg-blue-600 hover:bg-blue-700">
                            Return
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-2">
              {movements.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No movement history yet</p>
                </div>
              ) : (
                movements.map((mv) => (
                  <div
                    key={mv.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      mv.movement_type === 'Stock Added'
                        ? 'bg-emerald-50 border-emerald-200'
                        : mv.movement_type === 'Dispatch'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-slate-800">
                        {mv.movement_type === 'Stock Added' && '+'}
                        {mv.movement_type === 'Dispatch' && '-'}
                        {mv.movement_type === 'Return' && '+'}
                        {mv.quantity} {mv.garment_type}
                      </p>
                      <p className="text-xs text-slate-500">
                        {mv.movement_type} {mv.party_name && `• ${mv.party_name}`}
                      </p>
                      {mv.remarks && (
                        <p className="text-xs text-slate-400 italic">{mv.remarks}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{formatDate(mv.created_at)}</p>
                      <p className="text-xs text-slate-400">by {mv.staff_name}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
